/**
 * TypeScript types for Media Library.
 */

// =============================================================================
// Core Types
// =============================================================================

export interface MediaFolder {
  id: string;
  name: string;
  path: string;
  parent_folder_id: string | null;
  image_count: number;
  created_at: string;
  updated_at: string;
}

export interface MediaTag {
  id: string;
  name: string;
  color: string;
  image_count: number;
  created_at: string;
}

export interface ImageUsage {
  id: string;
  usage_type: 'campaign' | 'ad_group' | 'asset_group' | 'ad';
  campaign_id: string | null;
  campaign_name: string | null;
  ad_group_id: string | null;
  asset_group_id: string | null;
  ad_id: string | null;
  ad_type: string | null;  // RESPONSIVE_DISPLAY_AD, DEMAND_GEN_MULTI_ASSET_AD, etc.
  /** Ad platform — e.g. 'google_ads', 'meta_ads'. Defaults to 'google_ads' for legacy rows. */
  platform: string;
  /** Combined `${platform}.${image_field_type}` key, or null if no field type. */
  placement_key: string | null;
  google_ads_account_id: string | null;
  image_field_type: string | null;  // marketing_image, square_marketing_image, logo, etc.
  /** The Targeted MediaImage that was actually uploaded. NULL for legacy rows. */
  targeted_image_id: string | null;
  linked_at: string;
}

/**
 * Image categories. See `MEDIA_IMAGE_CATEGORIES` in
 * `src/flouri/gcloud_sql_client_db/models.py`.
 *
 * - `reference` — Creative Studio input only. Not selectable in the image selector.
 * - `approved`  — Ad-ready master. The only category eligible to feed the image selector.
 * - `targeted`  — Per-(platform, placement) crop derived from an Approved source.
 */
export type ImageCategory = 'reference' | 'approved' | 'targeted';

/** Crop rectangle in source-image pixel coordinates. */
export interface CropBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A user-confirmed crop that has NOT been persisted to GCS / DB yet.
 *
 * The TargetedImagePicker produces these client-side from the source image +
 * the user's drag interaction. Persisting only happens when the caller
 * decides to (e.g. PMax materializes at "Upload to Google Ads" click time;
 * the ad creation/edit surfaces materialize inline before passing IDs to
 * their parent form). This avoids the GCS-pollution failure mode where
 * every crop-adjust click writes a new Targeted blob, even ones the user
 * later abandons.
 *
 * Preview thumbnails for these are rendered via CSS (`background-image` on
 * `source.signed_url` with computed `background-size` / `background-position`)
 * — Canvas-cropping would require CORS-allowed access to the GCS bucket,
 * which we don't configure for the user-facing buckets.
 */
export interface PendingTargeted {
  /** The Approved source MediaImage the user picked. */
  source: MediaImage;
  /** Placement spec key (e.g. 'google_ads.square_marketing_image'). */
  placementKey: string;
  /** Crop box in source-image pixel coordinates. */
  cropBox: CropBox;
  /**
   * When this PendingTargeted is a *reuse* of an existing Targeted MediaImage
   * (picked from the Targeted tab in `TargetedImagePicker`, possibly
   * cross-placement), this is the originating Targeted row's id. Two
   * different existing Targeteds that share `(source.id, cropBox)` but
   * differ on origin placement_key would otherwise produce identical
   * pendingKeys — the shared `pendingKey()` helper below includes this id
   * when present so React keys (and remove-by-key logic) stay stable
   * across cross-placement picks.
   *
   * Undefined for the fresh-crop path (Approved tab → cropper) where
   * `(source.id, cropBox)` already disambiguates by construction.
   */
  existingTargetedId?: string;
}

/**
 * Stable identifier for a `PendingTargeted` so removal/key logic doesn't
 * rely on referential equality. Re-creating the array (e.g. parent
 * normalizing form state) would break `q !== p` filters; matching on
 * `(source.id, cropBox)` survives that since equal crops produce equal
 * keys.
 *
 * `existingTargetedId` (when present) prefixes the key so two
 * cross-placement reuses that share `(source.id, cropBox)` but differ on
 * origin Targeted row don't collide. Fresh-crop entries have no
 * `existingTargetedId` and fall back to the source+cropBox shape, which
 * already disambiguates by construction (each fresh crop produces a unique
 * box for its slot).
 *
 * Single source of truth — every consumer that needs a stable id for a
 * `PendingTargeted` (RDA, DemandGen, AssetGroup, Display, ImageAssets) must
 * import this helper rather than re-deriving the shape inline. Drift
 * between sites is a real risk: when cross-placement reuse landed, the
 * original local copies didn't include `existingTargetedId` and would
 * collide on multi-Targeted-with-same-box selections.
 */
export function pendingKey(p: PendingTargeted): string {
  const base = `${p.source.id}:${p.cropBox.x},${p.cropBox.y},${p.cropBox.w},${p.cropBox.h}`;
  return p.existingTargetedId ? `t:${p.existingTargetedId}|${base}` : base;
}

export interface MediaImage {
  id: string;
  filename: string;
  original_filename: string;
  signed_url: string;
  thumbnail_url: string | null; // Smaller thumbnail for fast loading in lists
  file_size_bytes: number;
  mime_type: string;
  width: number;
  height: number;
  alt_text: string | null;
  description: string | null;
  folder_id: string | null;
  folder_path: string | null;
  tags: MediaTag[];
  usages: ImageUsage[];
  usage_count: number;
  status: 'active' | 'archived' | 'deleted';
  // Category + targeted-only fields. `targeted_*` are populated only when category === 'targeted'.
  category: ImageCategory;
  source_image_id: string | null;
  platform: string | null;
  placement_key: string | null;
  crop_box: CropBox | null;
  crop_source: 'auto' | 'manual' | null;
  created_at: string;
  updated_at: string;
}

/**
 * Get the best URL to display for an image.
 * Prefers thumbnail_url for faster loading, falls back to signed_url.
 */
export function getDisplayUrl(image: MediaImage): string {
  return image.thumbnail_url || image.signed_url;
}

export interface StorageSummary {
  member_uuid: string;
  total_images: number;
  max_images: number;
  total_folders: number;
  total_tags: number;
  storage_used_bytes: number;
  storage_limit_bytes: number;
  images_by_folder: Record<string, number>;
  images_by_tag: Record<string, number>;
}

// =============================================================================
// Request Types
// =============================================================================

export interface CreateFolderRequest {
  member_uuid: string;
  name: string;
  parent_folder_id?: string;
}

export interface UpdateFolderRequest {
  member_uuid: string;
  name?: string;
  parent_folder_id?: string | null;
}

export interface CreateTagRequest {
  member_uuid: string;
  name: string;
  color?: string;
}

export interface UpdateImageRequest {
  member_uuid: string;
  folder_id?: string | null;
  alt_text?: string;
  description?: string;
  tag_ids?: string[];
}

export interface ListImagesRequest {
  member_uuid: string;
  folder_id?: string;
  tag_ids?: string[];
  search?: string;
  status?: 'active' | 'archived' | 'deleted';
  /** Filter by category. Omit to get Reference + Approved (Targeted excluded). */
  category?: ImageCategory;
  /** When listing Targeted images, restrict to a specific placement key. */
  placement_key?: string;
  /** When listing Targeted images, restrict to a specific source. */
  source_image_id?: string;
  /**
   * When listing Targeted images, surface every row reusable for the given
   * placement under the backend's upscale cap (same aspect ratio, parent
   * crop_box big enough, filesize under the placement's max). Server-side
   * superset of `placement_key=...`. Set on the `TargetedImagePicker`'s
   * Targeted tab so paginated listings don't silently drop compatible rows
   * past the page boundary.
   */
  compatible_with_placement_key?: string;
  page?: number;
  page_size?: number;
  sort_by?: 'created_at' | 'filename' | 'file_size_bytes' | 'original_filename';
  sort_order?: 'asc' | 'desc';
}

export interface BatchDeleteRequest {
  member_uuid: string;
  image_ids: string[];
}

export interface BatchMoveRequest {
  member_uuid: string;
  image_ids: string[];
  folder_id: string | null;
}

export interface BatchTagRequest {
  member_uuid: string;
  image_ids: string[];
  tag_ids: string[];
}

// =============================================================================
// Response Types
// =============================================================================

export interface ListImagesResponse {
  images: MediaImage[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  storage_used: number;
  storage_limit: number;
}

export interface BatchOperationResponse {
  success: boolean;
  affected_count: number;
  message: string;
}

export interface UploadImageResponse {
  success: boolean;
  image: MediaImage | null;
  message: string;
}

// =============================================================================
// UI State Types
// =============================================================================

export interface MediaLibraryFilters {
  folderId: string | null;
  tagIds: string[];
  search: string;
  sortBy: 'created_at' | 'filename' | 'file_size_bytes';
  sortOrder: 'asc' | 'desc';
}

export interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

// =============================================================================
// Helper Types
// =============================================================================

export type FolderTreeNode = MediaFolder & {
  children: FolderTreeNode[];
};

/**
 * Build a folder tree from a flat list of folders.
 */
export function buildFolderTree(folders: MediaFolder[]): FolderTreeNode[] {
  const map = new Map<string, FolderTreeNode>();
  const roots: FolderTreeNode[] = [];

  // Create nodes
  folders.forEach((folder) => {
    map.set(folder.id, { ...folder, children: [] });
  });

  // Build tree
  folders.forEach((folder) => {
    const node = map.get(folder.id)!;
    if (folder.parent_folder_id && map.has(folder.parent_folder_id)) {
      map.get(folder.parent_folder_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Get aspect ratio label for an image.
 */
export function getAspectRatioLabel(width: number, height: number): string {
  const ratio = width / height;
  if (Math.abs(ratio - 1) < 0.1) return 'Square';
  if (Math.abs(ratio - 1.91) < 0.1) return 'Landscape (1.91:1)';
  if (Math.abs(ratio - 1.78) < 0.1) return 'Landscape (16:9)';
  if (Math.abs(ratio - 0.8) < 0.1) return 'Portrait (4:5)';
  if (ratio > 1) return 'Landscape';
  return 'Portrait';
}
